import { useEffect } from "react";
import { VALUES, TEAM_LEAD, TEAM_ADVISORS, QUISOM_TEAM_IMG, MISSION_MAP_IMG } from "../data/site";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionLabel, ContactCTA } from "../components/Shared";

export default function QuiSom() {
  useEffect(() => {
    document.title = "Qui som — Orbis Forum Català";
  }, []);

  return (
    <div>
      <PageHero
        label="Qui som"
        title="Una firma privada al servei dels interessos catalans"
        subtitle="Independència, discreció i una vocació decididament internacional."
        image={QUISOM_TEAM_IMG}
      />

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <SectionLabel>La nostra missió</SectionLabel>
            </Reveal>
            <div className="lg:col-span-8 space-y-6">
              <Reveal>
                <p className="font-serif-display font-light text-2xl md:text-3xl leading-snug tracking-tight text-white/90 text-balance">
                  Orbis Forum Català és una consultora internacional privada creada per
                  defensar i facilitar els interessos dels ciutadans i les empreses de
                  Catalunya en l'àmbit internacional.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-white/55 font-light leading-relaxed">
                  Neixem amb la voluntat d'acompanyar particulars i organitzacions catalanes
                  en el desenvolupament dels seus projectes fora del territori, oferint
                  assessorament especialitzat, coordinació documental i suport internacional
                  amb un enfocament sobri i rigorós.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="text-white/55 font-light leading-relaxed">
                  Volem deixar-ho clar: <span className="text-white">no som una administració
                  pública, ni una ONG, ni un despatx d'advocats</span>. Som una firma privada
                  i independent, i la nostra única lleialtat és amb les persones i les empreses
                  que ens confien els seus projectes.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-ofc-navy/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <SectionLabel>Projecció global</SectionLabel>
              <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6 leading-tight">
                Una mirada internacional, sense fronteres
              </h2>
              <p className="mt-8 text-white/55 font-light leading-relaxed">
                Treballem amb una orientació decididament internacional, sense identificar
                cap territori concret com a seu principal. Aquesta neutralitat ens permet
                actuar amb flexibilitat allà on els vostres interessos ho requereixin.
              </p>
              <p className="mt-6 text-white/55 font-light leading-relaxed">
                La nostra xarxa i el nostre coneixement dels entorns internacionals ens
                permeten obrir portes, anticipar riscos i acompanyar cada projecte amb
                una perspectiva estratègica de llarg recorregut.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={MISSION_MAP_IMG} alt="Projecció internacional de Catalunya" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-ofc-black/20" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-ofc-navy/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionLabel>El nostre equip</SectionLabel>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6 max-w-2xl leading-tight">
              Una xarxa d'assessors amb presència internacional
            </h2>
            <p className="mt-6 text-white/55 font-light leading-relaxed max-w-2xl">
              Coordinats des de Catalunya, els nostres assessors aporten coneixement local a cada
              regió del món per acompanyar els vostres projectes amb precisió i discreció.
            </p>
          </Reveal>

          {/* Coordinador general */}
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center mt-16">
              <div className="md:col-span-4">
                <div className="relative overflow-hidden aspect-[3/4] group">
                  <img
                    src={TEAM_LEAD.image}
                    alt={TEAM_LEAD.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ofc-black/60 via-transparent to-transparent" />
                  <div className="absolute inset-0 border border-ofc-gold/0 group-hover:border-ofc-gold/40 transition-colors duration-500" />
                </div>
              </div>
              <div className="md:col-span-8">
                <span className="text-xs uppercase tracking-[0.3em] text-ofc-gold">Coordinació general</span>
                <h3 className="font-serif-display text-3xl md:text-4xl mt-4">{TEAM_LEAD.name}</h3>
                <p className="mt-2 text-sm uppercase tracking-[0.15em] text-white/60">
                  {TEAM_LEAD.role} · {TEAM_LEAD.location}
                </p>
                <p className="mt-6 text-white/55 font-light leading-relaxed max-w-2xl">{TEAM_LEAD.bio}</p>
              </div>
            </div>
          </Reveal>

          {/* Assessors internacionals */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/5 mt-12">
              {TEAM_ADVISORS.map((m) => (
                <div
                  key={m.name}
                  data-testid={`team-member-${m.name.split(" ")[0].toLowerCase()}`}
                  className="group bg-ofc-black"
                >
                  <div className="relative overflow-hidden aspect-[4/5]">
                    <img
                      src={m.image}
                      alt={m.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ofc-black/75 via-ofc-black/10 to-transparent" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-serif-display text-xl leading-tight">{m.name}</h4>
                    <p className="mt-2 text-[11px] uppercase tracking-[0.15em] text-ofc-gold">{m.role}</p>
                    <p className="mt-1 text-xs text-white/45">{m.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <p
              data-testid="team-photo-disclaimer"
              className="mt-10 text-xs text-white/40 font-light italic tracking-wide"
            >
              Nota: les fotografies de l'equip són imatges d'estudi retocades amb intel·ligència artificial.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionLabel>Els nostres valors</SectionLabel>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 mt-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-px w-12 bg-ofc-gold mb-6" />
                <h3 className="font-serif-display text-2xl">{v.title}</h3>
                <p className="mt-4 text-sm text-white/50 font-light leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </div>
  );
}
